import { useCallback, useEffect, useMemo, useState } from "react";
import { Stores, useGlobalState } from "react-migration/lib";
import {
  IComparablesTransactionDTO,
  buildCompsQuery,
  formatZooplaRecordAsLTComp,
  useComparableSearch,
} from "react-migration/domains/comparables";
import { Overview } from "./components";
import { useDebounce } from "usehooks-ts";
import {
  disabledEPCFields,
  disabledZooplaFields,
} from "react-migration/domains/comparables/util/DisabledFilters";
import { hiddenZooplaFields } from "react-migration/domains/comparables/util/hiddenFields";
import { ComparablesMapContainer } from "./components/ComparablesMapContainer";
import { isZoopla } from "react-migration/domains/comparables/util/isZoopla";

type GeoFilter = {
  max_dist: number;
  location: google.maps.LatLngLiteral;
};

interface ComparablesProps {
  site: { location: GeoJSON.Point };
}

export const Comparables = ({ site }: ComparablesProps) => {
  const [{ filters, priceMode }] = useGlobalState(Stores.comparables);
  const [geoFilter, setGeoFilter] = useState<GeoFilter | null>(null);
  const geoFilterDebounced = useDebounce(geoFilter, 500);

  const disabledFields = isZoopla(priceMode) ? disabledZooplaFields : disabledEPCFields;
  const hiddenFields = isZoopla(priceMode) ? hiddenZooplaFields : undefined;

  const { loading, data } = useComparableSearch({
    search: {
      ...buildCompsQuery(filters, disabledFields, hiddenFields),
      radius_geo_filter: geoFilterDebounced,
    },
    sort: {
      fieldName: "distance_from_center",
      sortType: {
        order: "asc",
      },
    },
    priceMode,
  });

  useEffect(() => {
    if (site.location) {
      setGeoFilter({
        max_dist: 500,
        location: {
          lat: site.location.coordinates[1],
          lng: site.location.coordinates[0],
        },
      });
    }
  }, [site]);

  const comparablesData: IComparablesTransactionDTO[] = useMemo(() => {
    const nodes = data.comparableSearch?.edges.map(({ node }) => node);
    return nodes ?? [];
  }, [data]);

  const zooplaComparablesData: IComparablesTransactionDTO[] = useMemo(() => {
    const nodes = data.zooplaComparableSearch?.edges.map(({ node }) => node);
    return nodes?.length && isZoopla(priceMode)
      ? formatZooplaRecordAsLTComp({
          results: nodes,
          priceMode,
        })
      : [];
  }, [data, priceMode]);

  const visibleIds = useMemo(() => {
    if (isZoopla(priceMode)) {
      return new Set(zooplaComparablesData.map(({ uprn }) => uprn ?? ""));
    }
    return new Set(comparablesData.map(({ transaction_id }) => transaction_id));
  }, [comparablesData, zooplaComparablesData, priceMode]);

  const onMaxDistChange = useCallback(
    (maxDist: number) => {
      if (geoFilter) {
        setGeoFilter({
          ...geoFilter,
          max_dist: maxDist,
        });
      }
    },
    [geoFilter, setGeoFilter]
  );
  const onFilterChange = useCallback(
    ({ radius, center }: { radius: number; center: google.maps.LatLngLiteral }) => {
      setGeoFilter({ max_dist: radius, location: center });
    },
    []
  );
  return (
    <div className="atlas-flex atlas-h-full atlas-p-4">
      <div className="atlas-w-1/2 atlas-pr-4" data-testid="comparables-map-container">
        <ComparablesMapContainer
          mapCircleConfig={{
            radius: geoFilter?.max_dist,
            center: geoFilter?.location,
            onCircleUpdate: onFilterChange,
          }}
          visibleIds={visibleIds}
        />
      </div>
      <div className="atlas-w-1/2 atlas-bg-neutral-200 atlas-border atlas-border-neutral-200 atlas-overflow-auto atlas-p-3">
        <Overview
          loading={!geoFilter || loading} // we need geoFilter here because it's null on initial render
          maxDistanceMeters={geoFilter?.max_dist ?? 0}
          onMaxDistanceMetersChange={onMaxDistChange}
          search={isZoopla(priceMode) ? zooplaComparablesData : comparablesData}
        />
      </div>
    </div>
  );
};
